import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { FileTextIcon } from "@phosphor-icons/react";

const LAST_UPDATED = "April 23, 2026";

export default function Terms() {
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="terms-page">
      <Navbar />
      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-12">
        <div className="flex items-center gap-3 text-[10px] font-mono tracking-widest text-zinc-500 uppercase">
          <FileTextIcon size={14} weight="bold" />
          Legal
        </div>
        <h1
          className="mt-3 font-display font-black text-4xl sm:text-5xl tracking-tighter"
          data-testid="terms-title"
        >
          Bidorah Terms &amp; Conditions
        </h1>
        <p className="mt-3 text-sm text-zinc-500">Last updated: {LAST_UPDATED}</p>

        <div className="prose prose-zinc max-w-none mt-10 text-[15px] leading-relaxed">
          <p>
            These Terms &amp; Conditions (&ldquo;Terms&rdquo;) govern your access
            to and use of <strong>Bidorah</strong>, including the Bidorah
            website, mobile experiences, live-streaming marketplace, and all
            related products and services (collectively, the
            &ldquo;Service&rdquo;). By creating a Bidorah account, applying to
            be a Bidorah seller, bidding in a Bidorah auction, or otherwise
            using the Service, you agree to be bound by these Terms. If you do
            not agree, do not use Bidorah.
          </p>

          <h2 className="font-display font-black text-2xl mt-10 mb-3 tracking-tight">
            1. Eligibility
          </h2>
          <p>
            You must be at least 18 years old and legally able to enter into a
            binding contract in your jurisdiction to use Bidorah. Wholesale
            activity on Bidorah additionally requires a valid Reseller Permit,
            registered business entity, and taxpayer identification as
            described in Section 4. Bidorah may, at its sole discretion, refuse
            service, suspend accounts, or remove listings that violate these
            Terms.
          </p>

          <h2 className="font-display font-black text-2xl mt-10 mb-3 tracking-tight">
            2. Your Bidorah account
          </h2>
          <p>
            To access most of Bidorah&rsquo;s features you must register for a
            Bidorah account. You agree to provide accurate, current, and
            complete information, to keep your credentials confidential, and to
            notify Bidorah immediately of any unauthorized access. You are
            responsible for all activity that occurs under your Bidorah
            account. Bidorah performs mandatory US photo-ID verification for
            buyers and sellers; failure to complete verification may limit your
            ability to bid, purchase, or sell.
          </p>

          <h2 className="font-display font-black text-2xl mt-10 mb-3 tracking-tight">
            3. Retail vs. wholesale segregation
          </h2>
          <p>
            Bidorah operates two strictly separated marketplaces:
            <strong> Retail</strong> and <strong>Wholesale</strong>. Wholesale
            sellers may not transact with retail buyers, and wholesale buyers
            may not purchase retail-segment listings. Bidorah enforces this
            segregation at the platform level and will reverse, cancel, or
            withhold transactions that violate it.
          </p>

          <h2 className="font-display font-black text-2xl mt-10 mb-3 tracking-tight">
            4. Wholesale buyer credential gate
          </h2>
          <p>
            Before bidding on or purchasing any wholesale listing on Bidorah,
            you must submit and have Bidorah approve the following credentials:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Registered business entity (LLC, S-Corp, C-Corp, Sole Proprietorship, or equivalent)</li>
            <li>Federal or state Taxpayer Identification Number (TIN / EIN)</li>
            <li>Valid Reseller&rsquo;s Permit for your jurisdiction</li>
          </ul>
          <p>
            Credentials are reviewed by Bidorah&rsquo;s compliance team.
            Approval is discretionary and may be revoked at any time if Bidorah
            reasonably suspects fraud, expired documentation, or violation of
            these Terms.
          </p>

          <h2 className="font-display font-black text-2xl mt-10 mb-3 tracking-tight">
            5. Listings, bids, and purchases
          </h2>
          <p>
            When you place a bid or make a purchase on Bidorah, you are
            entering into a binding commitment to buy the item at the listed or
            winning price, subject to these Terms. Live auctions close when
            Bidorah&rsquo;s systems end bidding; the highest eligible bid wins.
            Sellers are responsible for accurate descriptions, lawful sourcing,
            and timely shipment. Bidorah is not a party to the transaction
            between buyer and seller but operates as the platform facilitating
            it.
          </p>

          <h2 className="font-display font-black text-2xl mt-10 mb-3 tracking-tight">
            6. Payments, tips, and fees
          </h2>
          <p>
            Payments on Bidorah are processed by third-party providers
            including Stripe and PayPal. Bidorah collects platform fees
            disclosed at the time of sale. Viewers may send direct cash tips
            to sellers during a live stream; Bidorah passes 100% of each tip to
            the recipient seller (net of the payment processor&rsquo;s own
            fee) unless otherwise stated. All amounts are charged in U.S.
            dollars unless explicitly noted.
          </p>

          <h2 className="font-display font-black text-2xl mt-10 mb-3 tracking-tight">
            7. Shipping, returns, and disputes
          </h2>
          <p>
            Sellers must ship purchased items within the timeframe stated in
            the listing. Buyers may dispute a transaction through the Bidorah
            dispute process within the window disclosed at checkout. Bidorah
            holds funds in escrow for a defined review period before releasing
            them to sellers.
          </p>

          <h2 className="font-display font-black text-2xl mt-10 mb-3 tracking-tight">
            8. Prohibited conduct
          </h2>
          <p>When using Bidorah, you agree not to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>List, sell, or ship counterfeit, stolen, or illegal goods</li>
            <li>Manipulate auctions, shill-bid, or collude with other users</li>
            <li>Circumvent the retail/wholesale segregation enforced by Bidorah</li>
            <li>Attempt to bypass Bidorah&rsquo;s fee structure or redirect payments off-platform</li>
            <li>Harass, abuse, or threaten other Bidorah users, sellers, or staff</li>
            <li>Use Bidorah in violation of any applicable law or regulation</li>
          </ul>

          <h2 className="font-display font-black text-2xl mt-10 mb-3 tracking-tight">
            9. Intellectual property
          </h2>
          <p>
            The Bidorah name, logo, platform design, and all related content
            (excluding user-submitted listings and media) are the exclusive
            property of Bidorah or its licensors. You may not copy, modify, or
            distribute Bidorah&rsquo;s intellectual property without prior
            written consent. By uploading content to Bidorah (including listing
            photos, stream footage, and chat messages), you grant Bidorah a
            worldwide, non-exclusive, royalty-free license to host, display,
            and reproduce that content for the purpose of operating the
            Service.
          </p>

          <h2
            id="ai-grading"
            className="font-display font-black text-2xl mt-10 mb-3 tracking-tight"
          >
            10. AI Condition Grader &mdash; disclaimer &amp; limitations
          </h2>
          <div
            className="border-l-4 border-amber-400 bg-amber-50 text-zinc-900 p-4 rounded-r-md"
            data-testid="terms-ai-grader-disclaimer"
          >
            <p className="font-bold mb-2">
              AI grades are estimates only &mdash; not a certified grade.
            </p>
            <p className="text-sm">
              Bidorah&rsquo;s &ldquo;AI Condition Grader&rdquo; (also
              referred to as &ldquo;AI Grade&rdquo;, &ldquo;Bidoria
              Grade&rdquo;, or &ldquo;AI predicted grade&rdquo;) is an
              experimental machine-learning tool that produces an
              automated visual estimate from a single user-supplied
              photograph. It is provided for entertainment and rough
              reference only.
            </p>
          </div>
          <p>
            By using the AI Condition Grader, or by viewing any AI-grade
            estimate displayed on a Bidorah listing, stream, message, or
            shared link, you acknowledge and agree that:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Not a professional grade.</strong> The AI Condition
              Grader is <strong>not</strong> issued, endorsed, sponsored,
              certified, authenticated, or otherwise affiliated with
              Professional Sports Authenticator (&ldquo;PSA&rdquo;),
              Beckett Grading Services (&ldquo;BGS&rdquo;), Sportscard
              Guaranty Company (&ldquo;SGC&rdquo;), Certified Guaranty
              Company (&ldquo;CGC&rdquo;), or any other third-party
              grading or authentication service. References to PSA, BGS,
              SGC, CGC, or any third-party grading scale are used solely
              as familiar reference scales for the visible condition of
              the item and do not imply any third-party certification,
              examination, or guarantee.
            </li>
            <li>
              <strong>No guarantee of accuracy.</strong> AI-grade outputs
              are generated by a large-language-model vision pipeline
              that has known and unknown failure modes, including but not
              limited to lighting variance, image compression, glare,
              motion blur, partial occlusion, lookalike counterfeits,
              altered or doctored cards, sleeves, holders, custom inks,
              and items outside the model&rsquo;s training distribution.
              Bidorah makes no representation or warranty as to the
              accuracy, completeness, reliability, fitness for any
              particular purpose, or merchantability of any AI-grade
              output.
            </li>
            <li>
              <strong>Not authentication.</strong> The AI Condition
              Grader does not authenticate the item, does not verify
              that the item is genuine, does not detect alterations,
              trimming, recoloring, or counterfeiting, and does not
              guarantee that the photo depicts the actual item shipped.
              Buyers should always obtain professional third-party
              grading before relying on a grade for purchase, resale,
              insurance, or appraisal purposes.
            </li>
            <li>
              <strong>Not investment, financial, appraisal, or legal
              advice.</strong> No AI-grade output, predicted price band,
              or condition narrative should be relied upon as
              investment, financial, appraisal, insurance, or legal
              advice. Item values can vary significantly based on
              factors outside the AI Condition Grader&rsquo;s
              evaluation.
            </li>
            <li>
              <strong>Use at your own risk.</strong> You access and use
              the AI Condition Grader at your own risk. Any decision to
              buy, sell, list, price, ship, or insure an item based in
              whole or in part on an AI-grade output is your sole
              responsibility.
            </li>
            <li>
              <strong>Seller responsibility.</strong> Sellers who
              broadcast an AI-grade card or message on a Bidorah live
              show or listing remain solely responsible for the accuracy
              of their own item descriptions, photographs, condition
              statements, return policy, and refund obligations.
              Bidorah is not a party to the underlying transaction and
              does not warrant any seller&rsquo;s description of an
              item.
            </li>
            <li>
              <strong>Limitation of liability.</strong> To the maximum
              extent permitted by law, Bidorah, its affiliates,
              officers, employees, contractors, model providers, and
              licensors shall have no liability whatsoever for any
              direct, indirect, incidental, consequential, special,
              exemplary, or punitive damages, lost profits, lost
              revenue, lost data, or any other losses arising from or
              relating to your use of, or reliance on, any AI-grade
              output. Without limiting the foregoing, Bidorah&rsquo;s
              aggregate liability for any claim relating to the AI
              Condition Grader is capped at the amount specified in
              Section 11 (Disclaimers and limitation of liability)
              below.
            </li>
            <li>
              <strong>Indemnification.</strong> You agree to indemnify
              and hold harmless Bidorah, its affiliates, and their
              respective officers, employees, contractors, model
              providers, and licensors from any claim, demand, loss, or
              expense (including reasonable attorneys&rsquo; fees)
              arising from your use of the AI Condition Grader, your
              broadcast of any AI-grade output, or any misrepresentation
              you make based on or referencing an AI-grade output.
            </li>
            <li>
              <strong>Photos uploaded.</strong> Photos uploaded to the
              AI Condition Grader may be transmitted to third-party
              model providers for the sole purpose of generating the
              grade output and may be retained by Bidorah for service
              improvement and abuse-prevention purposes in accordance
              with the Bidorah Privacy Policy.
            </li>
          </ul>
          <p className="text-sm text-zinc-600 italic">
            For certified grading and authentication, buyers and sellers
            are encouraged to use a professional third-party grading
            service. Bidorah is not affiliated with any such service.
          </p>

          <h2 className="font-display font-black text-2xl mt-10 mb-3 tracking-tight">
            11. Disclaimers and limitation of liability
          </h2>
          <p>
            Bidorah is provided on an &ldquo;as is&rdquo; and &ldquo;as
            available&rdquo; basis, without warranties of any kind, express or
            implied. To the maximum extent permitted by law, Bidorah and its
            affiliates shall not be liable for indirect, incidental, special,
            consequential, or punitive damages arising out of or relating to
            your use of the Service. Bidorah&rsquo;s aggregate liability for
            any claim shall not exceed the greater of (a) the total fees
            Bidorah received from you in the twelve months preceding the
            claim, or (b) one hundred U.S. dollars (USD $100).
          </p>

          <h2 className="font-display font-black text-2xl mt-10 mb-3 tracking-tight">
            12. Termination
          </h2>
          <p>
            Bidorah may suspend or terminate your access to the Service at any
            time, with or without notice, for conduct that Bidorah reasonably
            believes violates these Terms or is harmful to other Bidorah users
            or to Bidorah itself. You may terminate your Bidorah account at
            any time by contacting support.
          </p>

          <h2
            id="ip-and-device-bans"
            className="font-display font-black text-2xl mt-10 mb-3 tracking-tight"
          >
            13. Account, IP, and device bans
          </h2>
          <p>
            To keep Bidorah safe for verified sellers and buyers, we operate
            three layers of enforcement. This section explains, in plain
            language, what we collect, when we ban, and how to appeal.
          </p>

          <h3 className="font-display font-bold text-lg mt-6 mb-2">
            13.1 What signals we collect
          </h3>
          <p>
            When you use Bidorah we record, alongside the data described in
            our Privacy Policy:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 my-3">
            <li>
              The <b>IP address</b> your connection originates from at signup,
              ID verification, login, and during meaningful actions (bidding,
              purchasing, going live).
            </li>
            <li>
              A <b>browser / device fingerprint</b> — a hashed signature
              derived from publicly-visible attributes of your browser or
              app (canvas rendering, screen size, language, user agent,
              available fonts, etc.). The fingerprint is a one-way hash;
              we cannot reverse it back to those underlying values, and it
              does not identify <i>you</i>, only your browser instance.
            </li>
            <li>
              An optional <b>native device identifier</b> on the official
              Bidorah Android and iOS apps, when those apps expose one.
            </li>
            <li>
              <b>Coarse geolocation</b> (country, region, ISP/ASN) derived
              from your IP via an industry-standard GeoIP service.
            </li>
          </ul>

          <h3 className="font-display font-bold text-lg mt-6 mb-2">
            13.2 Why we may ban an account, IP, or device
          </h3>
          <p>
            We may ban an account, the IP address(es) associated with it, the
            device fingerprint(s) it has used, or any combination of these,
            when the underlying behavior includes (without limitation):
          </p>
          <ul className="list-disc pl-6 space-y-1.5 my-3">
            <li>
              Fraud, attempted fraud, chargeback abuse, or payment-method
              testing.
            </li>
            <li>
              Operating multiple accounts to evade a prior ban, manipulate
              auctions (shill or sniper rings), or circumvent buyer/seller
              segregation (e.g. wholesale-only listings).
            </li>
            <li>
              Submitting forged, doctored, or fraudulent ID-verification
              materials.
            </li>
            <li>
              Harassment, hate speech, threats, sexual content, doxxing, or
              any conduct that violates our Community Guidelines.
            </li>
            <li>
              Counterfeit, stolen, recalled, or illegal goods.
            </li>
            <li>
              Automated abuse (scraping, credential stuffing, denial-of-service
              behavior, brute-forcing).
            </li>
            <li>
              Court order, law-enforcement request, or applicable legal
              requirement.
            </li>
          </ul>

          <h3 className="font-display font-bold text-lg mt-6 mb-2">
            13.3 How bans work
          </h3>
          <ul className="list-disc pl-6 space-y-1.5 my-3">
            <li>
              An <b>account ban</b> blocks the specific user from logging in
              or performing any authenticated action. Open orders may still
              be fulfilled at our discretion.
            </li>
            <li>
              An <b>IP ban</b> blocks any traffic — authenticated or not —
              originating from the listed IP from reaching Bidorah's API.
              IP bans may be permanent or time-limited.
            </li>
            <li>
              A <b>device-fingerprint ban</b> blocks any browser or app whose
              fingerprint matches the listed value, regardless of which
              account it logs into. This is the primary tool for stopping
              ban-evading sockpuppets.
            </li>
            <li>
              Bans propagate across our backend within approximately 30
              seconds. Lifting a ban takes effect within the same window.
            </li>
          </ul>

          <h3 className="font-display font-bold text-lg mt-6 mb-2">
            13.4 Collateral effects
          </h3>
          <p>
            IP and device bans can occasionally affect users who share an
            otherwise-clean connection or device with a banned user
            (household members, public Wi-Fi, corporate networks). If you
            believe you are blocked because of someone else's behavior on
            your network or device, contact support — see 13.6.
          </p>

          <h3 className="font-display font-bold text-lg mt-6 mb-2">
            13.5 Ban duration
          </h3>
          <p>
            Unless we specify otherwise, bans are <b>indefinite</b>. We
            review every appeal individually and may shorten, lift, or
            convert a ban based on the facts of the case. We do not
            publish a fixed ban schedule because doing so would let bad
            actors plan around it.
          </p>

          <h3 className="font-display font-bold text-lg mt-6 mb-2">
            13.6 Appeals
          </h3>
          <p>
            If you believe a ban affecting you is mistaken or
            disproportionate, you can appeal by emailing{" "}
            <a
              href="mailto:appeals@bidorah.com"
              className="text-[#1D78AB] underline font-bold"
            >
              appeals@bidorah.com
            </a>
            {" "}from any email address you control. Please include, if you
            know them:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 my-3">
            <li>The Bidorah username or email of the banned account.</li>
            <li>The approximate date and time the ban took effect.</li>
            <li>
              A brief, plain-language description of what you believe
              happened and why the ban should be lifted.
            </li>
          </ul>
          <p>
            We aim to respond to every appeal within <b>5 business days</b>.
            Appeals related to fraud, court orders, or law-enforcement
            requests may take longer.
          </p>

          <h3 className="font-display font-bold text-lg mt-6 mb-2">
            13.7 No private right of action
          </h3>
          <p>
            Bidorah's ban decisions are made in our reasonable discretion to
            protect the integrity of the marketplace. Nothing in this section
            creates a right to access Bidorah, to a specific outcome on an
            appeal, or to compensation for the effects of a ban. Bidorah
            reserves the right to refuse service to anyone, with or without
            stated cause, in accordance with applicable law.
          </p>

          <h2 className="font-display font-black text-2xl mt-10 mb-3 tracking-tight">
            14. Governing law &amp; dispute resolution
          </h2>
          <p>
            These Terms are governed by the laws of the State of Delaware,
            without regard to its conflict-of-laws principles. Any dispute
            arising from these Terms or your use of Bidorah shall be resolved
            through binding arbitration in Delaware, except that either party
            may bring individual claims in small-claims court where eligible.
          </p>

          <h2 className="font-display font-black text-2xl mt-10 mb-3 tracking-tight">
            14. Changes to these Terms
          </h2>
          <p>
            Bidorah may update these Terms from time to time. Material changes
            will be posted on the Bidorah platform with an updated
            &ldquo;Last updated&rdquo; date. Your continued use of Bidorah
            after a change constitutes acceptance of the revised Terms.
          </p>

          <h2 className="font-display font-black text-2xl mt-10 mb-3 tracking-tight">
            15. Contact
          </h2>
          <p>
            Questions about these Terms? Reach the Bidorah team at{" "}
            <a
              href="mailto:legal@bidorah.com"
              className="text-[#1D78AB] font-semibold hover:underline"
            >
              legal@bidorah.com
            </a>
            .
          </p>

          <p className="mt-10 text-xs text-zinc-500">
            © {new Date().getFullYear()} Bidorah. All rights reserved.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
